/**
 * REST calls behind the end-of-call "Review participants" sheet.
 */
import { apiClient } from './apiService';
import { fetchOwnSessionRecord } from './ownSessionRecord';
import { ParticipantEdit, ReviewRosterEntry, ReviewTranscript } from '../utils/participantReview';

export interface CallRecord {
  participants: ReviewRosterEntry[];
  transcripts: ReviewTranscript[];
}

export interface ParticipantUpdateResult {
  participants?: ReviewRosterEntry[];
  reanalysisQueued?: boolean;
}

export const SAVE_PARTICIPANTS_FALLBACK_ERROR = 'Failed to update participants. Please try again.';

/** The rep is waiting on "Removing bot..." — never hold the summary longer than this. */
const CALL_RECORD_TIMEOUT_MS = 5000;

/**
 * The saved call record (full roster + every transcript). The live store keeps
 * only the last 50 transcripts, so a participant who spoke early in a long call
 * is visible only here. Same owner-only endpoint the page resumes from, read
 * with the user's Bearer token. Returns null when the record is missing or
 * unreachable; the caller falls back to the live store.
 */
export const fetchCallRecord = async (sessionId: string): Promise<CallRecord | null> => {
  const record = await fetchOwnSessionRecord(sessionId, CALL_RECORD_TIMEOUT_MS);
  const transcripts = record?.transcripts;
  if (!Array.isArray(transcripts) || transcripts.length === 0) return null;
  return {
    participants: (record?.participants || []) as ReviewRosterEntry[],
    transcripts: transcripts as ReviewTranscript[],
  };
};

/** PUT the changed participants. Throws `{ message }` (the server's reason) on failure. */
export const saveParticipantEdits = async (
  sessionId: string,
  participants: ParticipantEdit[],
): Promise<ParticipantUpdateResult | undefined> => {
  const response = await apiClient.put<ParticipantUpdateResult>(
    `/conversations/sessions/${encodeURIComponent(sessionId)}/participants`,
    { participants },
  );
  return response?.data;
};
